import { alpha, MenuItem, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { ChatTestPersonaGroup, fetchChatTestPersonas } from 'external/firebase/controllers/chat';

// Super-admins only (locally and in production), so the labels are plain
// English rather than locale keys.
const ROLE_OPTIONS = [
    { value: 'self', label: 'Myself (real role)' },
    { value: 'user', label: 'Student' },
    { value: 'dėstytojas', label: 'Lecturer' },
    { value: 'kuratorius', label: 'Curator' },
    { value: 'admin', label: 'Admin' },
];

const groupLabel = (group: ChatTestPersonaGroup): string => {
    const name = group.name === group.id ? group.id : `${group.name} (${group.id})`;
    return `${name} — ${group.courseCount} courses, ${group.unlockedModuleCount} modules open`;
};

interface ChatTestAsBarProps {
    disabled: boolean;
    onChange: (testAs: string[] | null) => void;
}

// Each change starts a new chat: the chat function gives every persona its own
// conversation owner, so an existing session would come back as a conflict.
const ChatTestAsBar: React.FunctionComponent<ChatTestAsBarProps> = ({ disabled, onChange }) => {
    const [groups, setGroups] = useState<ChatTestPersonaGroup[]>([]);
    const [role, setRole] = useState('self');
    const [groupId, setGroupId] = useState('');

    useEffect(() => {
        fetchChatTestPersonas().then(setGroups);
    }, []);

    const apply = (nextRole: string, nextGroupId: string) => {
        setRole(nextRole);
        setGroupId(nextGroupId);
        if (nextRole === 'self') {
            onChange(null);
            return;
        }
        onChange(nextGroupId ? [nextRole, nextGroupId] : [nextRole]);
    };

    return (
        <Stack spacing={1} sx={{ px: 2, py: 1, bgcolor: theme => alpha(theme.palette.warning.main, 0.12) }}>
            <Typography variant='caption' color='text.secondary'>
                Test mode — answer as another role or cohort
            </Typography>
            <Stack direction='row' spacing={1}>
                <TextField
                    select
                    size='small'
                    label='Role'
                    value={role}
                    disabled={disabled}
                    onChange={event => apply(event.target.value, role === 'self' ? '' : groupId)}
                    sx={{ minWidth: 140 }}
                >
                    {ROLE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size='small'
                    label='Group'
                    value={groupId}
                    disabled={disabled || role === 'self'}
                    onChange={event => apply(role, event.target.value)}
                    fullWidth
                >
                    <MenuItem value=''>No group</MenuItem>
                    {groups.map(group => (
                        <MenuItem key={group.id} value={group.id}>
                            {groupLabel(group)}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
        </Stack>
    );
};

export default ChatTestAsBar;
